import React, { useEffect } from "react";
import { Modal, Button, Form, Row, Col, Toast, ToastContainer } from "react-bootstrap";
import { useForm, Controller } from "react-hook-form";
import PhoneInput from "react-phone-number-input";
import "react-phone-number-input/style.css";
import { useUserStore } from "../../../store/user-store";
import { useDataFetch } from "../../../hooks/use-data-fetch";

interface ValidationModalFormProps {
  show: boolean;
  onClose: () => void;
  handleNext: () => void;
}

enum DocumentType {
  CC = "CC",
  CE = "CE",
  PASSPORT = "PASSPORT",
}

interface FormData {
  document: string;
  documentType?: DocumentType;
  cellphone: string;
}


const ValidationModalForm: React.FC<ValidationModalFormProps> = ({ show, onClose,handleNext }) => {
  const user = useUserStore((state) => state.user);
  const setUser = useUserStore(state => state.setUser);
  const { patchData: usePatch } = useDataFetch();
  const { mutateAsync: updateUser } = usePatch("/users/update/user", {});
    
  const { register, handleSubmit, control, reset, formState: { errors, isSubmitting } } = useForm<FormData>();

  const [showToast, setShowToast] = React.useState(false);
  const [toastMessage, setToastMessage] = React.useState("");
  const [toastVariant, setToastVariant] = React.useState<"success" | "danger">("success");

  useEffect(() => {
    if (user) {
      reset({
        document: user.document || "",
        documentType: user.documentType || undefined  ,
        cellphone: user.cellphone ? String(user.cellphone) : "",
      });
    }
  }, [user, reset]);

  const onSubmit = async (data: FormData) => {
    if (!user) return;

    try {
        const body = {
            documentType: data.documentType,
            cellphone: data.cellphone,
            document: data.document,
        };
        await updateUser(body);
            setUser({
            ...user,
            ...body,
        });
        setToastMessage("Datos actualizados correctamente");
        setToastVariant("success");
        setShowToast(true);
        onClose();
        handleNext();
    } catch (error: any) {
      setToastMessage(error.message || "Error al actualizar datos");
      setToastVariant("danger");
      setShowToast(true);
    }
  };

  return (
    <>
      <Modal
        show={show}
        onHide={onClose}
        centered
        className="text-white"
        backdrop="static"  
        keyboard={false} 
    >
        <Modal.Header className="text-white">
          <Modal.Title>Complete los datos obligatorios</Modal.Title>
        </Modal.Header>
        <Modal.Body className="bg-dark text-white">
          <Form onSubmit={handleSubmit(onSubmit)}>
            <Row>
              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Tipo de documento</Form.Label>
                  <Form.Select
                    {...register("documentType", {
                      required: "El tipo de documento es obligatorio",
                    })}
                    isInvalid={!!errors.documentType}
                  >
                    <option value="">Seleccione un tipo</option>
                    <option value={DocumentType.CC}>Cédula de ciudadanía</option>
                    <option value={DocumentType.CE}>Cédula de extranjería</option>
                    <option value={DocumentType.PASSPORT}>Pasaporte</option>
                  </Form.Select>

                  <Form.Control.Feedback type="invalid" className="text-warning">
                    {errors.documentType?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Documento</Form.Label>
                  <Form.Control
                    type="text"
                    {...register("document", {
                      required: "El documento es obligatorio",
                      minLength: {
                        value: 6,
                        message: "El documento debe tener al menos 6 dígitos",
                      },
                      maxLength: {
                        value: 12,
                        message: "El documento no puede exceder 12 dígitos",
                      },
                      pattern: {
                        value: /^[0-9]+$/,
                        message: "El documento solo puede contener números",
                      },
                    })}
                    isInvalid={!!errors.document}
                    className="bg-secondary text-white border-light"
                  />
                  <Form.Control.Feedback type="invalid" className="text-warning">
                    {errors.document?.message}
                  </Form.Control.Feedback>
                </Form.Group>
              </Col>

              <Col md={12} className="mb-3">
                <Form.Group>
                  <Form.Label>Teléfono</Form.Label>
                  <Controller
                    name="cellphone"
                    control={control}
                    rules={{ required: "El teléfono es obligatorio" }}
                    render={({ field, fieldState }) => (
                      <>
                        <PhoneInput
                          {...field}
                          defaultCountry="CO"
                          value={field.value || ""}
                          onChange={field.onChange}
                          className={`form-control bg-secondary text-white border-light ${fieldState.invalid ? "is-invalid" : ""}`}
                        />
                        {fieldState.error && (
                          <div className="invalid-feedback d-block text-warning">
                            {fieldState.error.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </Form.Group>
              </Col>
            </Row>

            <Button type="submit" variant="primary" disabled={isSubmitting}>
              {isSubmitting ? "Guardando..." : "Guardar datos"}
            </Button>
          </Form>
        </Modal.Body>
      </Modal>

      <ToastContainer position="bottom-end" className="p-3">
        <Toast
          bg={toastVariant === "success" ? "success" : "danger"}
          show={showToast}
          onClose={() => setShowToast(false)}
          delay={3000}
          autohide
        >
          <Toast.Body className="text-white">{toastMessage}</Toast.Body>
        </Toast>
      </ToastContainer>
    </>
  );
};

export default ValidationModalForm;
